import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';

import { SyncStep, SyncStepState } from '../../../models/permit-sync.model';

export interface SyncProgressDialogData {
    steps$: BehaviorSubject<SyncStep[]>;
    progress$: BehaviorSubject<number>;
    done$: BehaviorSubject<boolean>;
}

/**
 * Presentational only: it renders the state the caller pushes and makes no HTTP
 * call of its own. The sequence lives in PermitConfigComponent, which owns the
 * ViewChild needed for the final list refresh.
 */
@Component({
    selector: 'app-permit-sync-progress-dialog',
    templateUrl: './permit-sync-progress-dialog.component.html',
    styleUrls: ['./permit-sync-progress-dialog.component.scss'],
})
export class PermitSyncProgressDialogComponent {
    constructor(@Inject(MAT_DIALOG_DATA) public data: SyncProgressDialogData) {}

    /** Material icon per step state; `active` renders a spinner instead. */
    icon(state: SyncStepState): string {
        switch (state) {
            case 'ok':
                return 'check_circle';
            case 'running':
                return 'schedule';
            case 'failed':
                return 'error';
            default:
                return 'radio_button_unchecked';
        }
    }

    labelKey(step: SyncStep): string {
        return `permit.config.categories.sync_step_${step.key}`;
    }

    /**
     * A `running` step is not a failure — the connector kept working after our
     * request gave up waiting — so it gets its own explanatory line.
     *
     * A failure is only explained when the step says why. Keying this on the step
     * ("the first one means no connector") was wrong: the backend's 400 lands on
     * whichever sync step was running when the configuration disappeared, and a
     * plain connector failure must not be mislabelled as a missing connector.
     */
    hintKey(step: SyncStep): string | null {
        if (step.state === 'running') return 'permit.config.categories.sync_step_running_hint';
        if (step.state === 'failed' && step.reason === 'not-configured') {
            return 'permit.config.categories.sync_not_configured';
        }
        return null;
    }
}
